/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Badge, Flex, Link, Table } from '@radix-ui/themes';
import { useRouter } from '@uirouter/react';

import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';
import type { EvaluationRow } from './evaluations.types';
import { RepositoryReevaluateAction } from './RepositoryReevaluateAction';
import { useRepositoryReevaluation } from './useRepositoryReevaluation';

// The evaluation detail route. Its {repositoryId} path parameter takes the repository's public id (its Nexus
// name), which the backend resolves to IQ's internal id; row.name carries that public id.
const DETAIL_STATE = 'preview.security.evaluationDetail';

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** The browser's local time, or the "never" text when IQ has no evaluation or sent something unreadable. */
function formatEvaluationTime(value: string | null): string {
  if (!value) return STRINGS.values.neverEvaluated;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? STRINGS.values.neverEvaluated : TIME_FORMAT.format(date);
}

interface ThreatCountBadgeProps {
  count: number;
  color: 'red' | 'orange' | 'yellow';
  label: string;
}

function ThreatCountBadge({ count, color, label }: ThreatCountBadgeProps): JSX.Element {
  return (
    <Badge
      size="1"
      radius="full"
      color={count > 0 ? color : 'gray'}
      variant={count > 0 ? 'solid' : 'soft'}
      title={STRINGS.countTitle(count, label)}
      style={{ minWidth: 35, justifyContent: 'center' }}
    >
      {count.toLocaleString()}
    </Badge>
  );
}

export interface EvaluationsTableRowProps {
  row: EvaluationRow;
  /** The user may re-evaluate repositories: the row gets the action. */
  canEvaluate: boolean;
  /** Called when a re-evaluation ends, so the list shows the new counts. */
  onReevaluated: () => void;
}

/**
 * One repository of the list. It owns that repository's re-evaluation, so a run survives a refresh of the list while
 * the row stays on the page. When the row leaves it (sort, filter, page change) the watch ends with it; the run goes on
 * in IQ and the row shows "In progress" again from the list data.
 */
export function EvaluationsTableRow({ row, canEvaluate, onReevaluated }: EvaluationsTableRowProps): JSX.Element {
  const router = useRouter();
  const reevaluation = useRepositoryReevaluation(row.repositoryId, row.reevaluationRunning, onReevaluated);
  // NEXUS-55569: each listed repository opens its evaluation detail page. The whole row is clickable and the name is
  // a real link (keyboard-focusable, open-in-new-tab), both routing to the same detail state.
  const detailParams = { repositoryId: row.name };
  const open = () => router.stateService.go(DETAIL_STATE, detailParams);

  return (
    <Table.Row data-testid={`evaluations-row-${row.name}`} className="evaluations-row--clickable" onClick={open}>
      <Table.Cell>
        <Flex align="center" gap="2">
          <Link
            size="2"
            href={router.stateService.href(DETAIL_STATE, detailParams)}
            data-analytics-id="nxrm-security-evaluations-row-open-lnk"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              open();
            }}
          >
            {row.name}
          </Link>
          {reevaluation.running && (
            <Badge color="blue" variant="soft" size="1">
              {STRINGS.values.inProgress}
            </Badge>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell>{row.format}</Table.Cell>
      <Table.Cell>{row.quarantineEnabled ? STRINGS.values.yes : STRINGS.values.no}</Table.Cell>
      <Table.Cell>{formatEvaluationTime(row.lastEvaluationTime)}</Table.Cell>
      <Table.Cell>{row.componentsEvaluated.toLocaleString()}</Table.Cell>
      <Table.Cell>
        <ThreatCountBadge count={row.criticalViolationCount} color="red" label={STRINGS.columns.critical} />
      </Table.Cell>
      <Table.Cell>
        <ThreatCountBadge count={row.severeViolationCount} color="orange" label={STRINGS.columns.severe} />
      </Table.Cell>
      <Table.Cell>
        <ThreatCountBadge count={row.moderateViolationCount} color="yellow" label={STRINGS.columns.moderate} />
      </Table.Cell>
      <Table.Cell>{row.quarantinedComponentCount.toLocaleString()}</Table.Cell>
      {canEvaluate && (
        // The row opens the detail page on click: the button and its dialog (a portal, whose events still bubble
        // through React to here) must not.
        <Table.Cell onClick={(event) => event.stopPropagation()}>
          {/* A row IQ sent without an id has nothing to re-evaluate: keep the cell so the columns stay aligned. */}
          {row.repositoryId && (
            <RepositoryReevaluateAction
              repositoryName={row.name}
              disabled={reevaluation.running}
              confirmOpen={reevaluation.confirmOpen}
              onOpen={reevaluation.openConfirm}
              onClose={reevaluation.closeConfirm}
              onConfirm={reevaluation.confirm}
            />
          )}
        </Table.Cell>
      )}
    </Table.Row>
  );
}
